(() => {
  const register = () => {
    if (window.eurusCookieBannerRegistered) return;
    window.eurusCookieBannerRegistered = true;

    let privacyLoading;
    const getPrivacy = () => {
      const api = window.Shopify?.customerPrivacy;
      if (api?.currentVisitorConsent && api?.shouldShowBanner) return Promise.resolve(api);
      if (!privacyLoading) {
        privacyLoading = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Privacy API unavailable')), 10000);
          const loaded = (error) => {
            clearTimeout(timeout);
            const privacy = window.Shopify?.customerPrivacy;
            if (error || !privacy?.currentVisitorConsent || !privacy?.shouldShowBanner) {
              reject(new Error('Privacy API unavailable'));
            } else {
              resolve(privacy);
            }
          };
          try {
            window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], loaded);
          } catch (error) {
            loaded(error);
          }
        }).catch((error) => {
          privacyLoading = null;
          throw error;
        });
      }
      return privacyLoading;
    };

    let privacyRestoring;
    const restorePrivacy = (api, force = false) => {
      let hasNavigationConsent = false;
      try {
        hasNavigationConsent = window.performance.getEntriesByType('navigation')[0]?.serverTiming?.some((entry) => entry.name === '_cmp' && entry.description);
      } catch (_) {}
      if (!force && (api.cachedConsent || hasNavigationConsent)) return Promise.resolve();
      if (privacyRestoring) return privacyRestoring;

      const initialCache = api.cachedConsent;
      privacyRestoring = (async () => {
        const controller = new AbortController();
        let timeout;
        try {
          const response = await Promise.race([
            fetch(window.location.href, { method: 'HEAD', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal }),
            new Promise((_, reject) => {
              timeout = setTimeout(() => {
                controller.abort();
                reject(new Error('Consent state request timed out'));
              }, 8000);
            })
          ]);
          if (!response.ok) throw new Error('Consent state request failed');
          const header = response.headers.get('server-timing') || '';
          const consent = header.match(/(?:^|,)\s*_cmp\s*;\s*desc="([^"]*)"/)?.[1];
          if (!consent || !/^3[a-z.]*_/i.test(decodeURIComponent(consent))) throw new Error('Unsupported consent state');
          if (window.Shopify.customerPrivacy !== api || api.cachedConsent !== initialCache) return;
          // Shopify Hydrogen uses this API cache to supply server-provided consent.
          // Keep the server value intact; never re-submit or synthesize a decision.
          api.cachedConsent = consent;
        } finally {
          clearTimeout(timeout);
        }
      })().finally(() => { privacyRestoring = null; });
      return privacyRestoring;
    };

    Alpine.data('xCookieBanner', (delay) => ({
      show: false,
      ready: false,
      saving: false,
      error: '',
      initialized: false,
      destroyed: false,
      init() {
        if (this.initialized) return;
        this.initialized = true;
        this.onConsent = () => {
          // Shopify emits this before the save callback. Wait for that callback
          // when this component is saving, including when the visitor declines.
          if (!this.saving && this.ready) this.syncVisibility();
        };
        this.onPageShow = (event) => { if (event.persisted) this.refresh(true); };
        document.addEventListener('visitorConsentCollected', this.onConsent);
        window.addEventListener('pageshow', this.onPageShow);
        this.timer = setTimeout(() => this.refresh(), delay * 1000);
      },
      async refresh(force = false) {
        if (this.destroyed || this.saving) return;
        try {
          const api = await getPrivacy();
          await restorePrivacy(api, force);
          if (this.destroyed || this.saving) return;
          this.ready = true;
          if (this.error === 'load') this.error = '';
          this.syncVisibility();
          document.dispatchEvent(new CustomEvent('eurus:customer-privacy:loaded'));
        } catch (_) {
          if (this.destroyed) return;
          this.ready = false;
          this.show = true;
          this.error = 'load';
        }
      },
      syncVisibility() {
        // Shopify's regional banner flag can remain true after a decision.
        // Read the current decision from the API, including legacy choices
        // that recorded analytics and marketing without preferences.
        try {
          const api = window.Shopify.customerPrivacy;
          const consent = api.currentVisitorConsent();
          const hasDecision = ['analytics', 'marketing'].every((key) => ['yes', 'no'].includes(consent[key]));
          this.show = api.shouldShowBanner() && !hasDecision;
        } catch (_) {
          this.ready = false;
          this.show = true;
          this.error = 'load';
        }
      },
      handleAccept() {
        return this.saveConsent({ analytics: true, marketing: true, preferences: true });
      },
      handleDecline() {
        return this.saveConsent({ analytics: false, marketing: false, preferences: false });
      },
      async saveConsent(consent) {
        if (this.saving || !this.ready || this.destroyed) return;
        this.saving = true;
        this.error = '';
        try {
          const api = await getPrivacy();
          await new Promise((resolve, reject) => {
            const request = api.setTrackingConsent(consent, (result) => {
              if (result?.error || result instanceof Error) reject(new Error('Consent save failed'));
              else resolve();
            });
            // Also handle API versions returning a rejected promise.
            request?.catch?.(reject);
          });
          const saved = api.currentVisitorConsent();
          if (!Object.keys(consent).every((key) => saved[key] === (consent[key] ? 'yes' : 'no'))) {
            throw new Error('Consent was not confirmed');
          }
          if (this.destroyed) return;
          this.show = false;
        } catch (_) {
          if (!this.destroyed) this.error = 'save';
        } finally {
          this.saving = false;
        }
      },
      destroy() {
        this.destroyed = true;
        clearTimeout(this.timer);
        document.removeEventListener('visitorConsentCollected', this.onConsent);
        window.removeEventListener('pageshow', this.onPageShow);
      }
    }));
  };

  if (window.Alpine) register();
  else document.addEventListener('alpine:init', register, { once: true });
})();
